import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import takeWhile from 'lodash/takeWhile';
import takeRightWhile from 'lodash/takeRightWhile';
import { Header } from 'formlabs-web-components/Type';

import Filter, { InputFilter } from './Filter';
import DateFilter from './DateFilter';
import FilterSidebar from './FilterSidebar';
import clientOnly from '../../hoc/clientOnly';
import { forceCheck } from 'react-lazyload';

const conditionalFilter = (arr, condition, func) => {
  return condition ? arr.filter(func) : arr;
};

const createOptionsFilter = (labelText, options, paramsKey) => {
  return () => ({
    loading: false,
    data: {
      label: labelText,
      options: options.map(option => ({ value: option[0], label: option[1] })),
      paramsKey
    }
  });
};

const updateOptionsFilter = (current, set) => {
  return (id, values) => {
    const removeValues = values.filter(value => current.includes(value));
    const updated =
      removeValues.length > 0
        ? current.filter(cur => !removeValues.includes(cur))
        : [...new Set(current.concat(values))];
    set(updated);
  };
};

const EventsSection = ({ events, label }) => {
  return events.length ? (
    <div style={{ marginBottom: '1rem' }}>
      <Header styles={{ marginBottom: '1rem' }} displayAs={4} level={2}>
        {`${label} (${events.length})`}
      </Header>
      {events}
    </div>
  ) : null;
};

const EventCardsFilter = ({ children }) => {
  const { t } = useTranslation();
  const [industries, setIndustries] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [titleFilter, setTitle] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const industriesList = [
    ['General', t('General')],
    ['Audiology', t('Audiology')],
    ['Dental', t('Dental')],
    ['Education', t('Education')],
    ['Engineering & Product Design', t('Engineering & Product Design')],
    ['Entertainment', t('Entertainment')],
    ['Jewelry', t('Jewelry')],
    ['Manufacturing', t('Manufacturing')],
    ['Medical', t('Medical')]
  ];
  const industriesKey = 'Industry';

  const eventTypesList = [
    ['Office Hours', t('Office Hours')],
    ['Demo Session', t('Demo Session')],
    ['Webinar', t('Webinar')],
    ['Formlabs User Summit', t('Formlabs User Summit')]
  ];

  const eventTypesKey = 'Event Types';

  const updateIndustriesFilter = updateOptionsFilter(industries, setIndustries);
  const updateEventTypesFilter = updateOptionsFilter(eventTypes, setEventTypes);

  const updateTitleFilter = (id, value) => {
    setTitle(value);
  };

  const updateDateFilter = (which, date) => {
    which === 'start' ? setStartDate(date) : setEndDate(date);
  };

  const activeFilterCount =
    industries.length +
    eventTypes.length +
    (titleFilter === '' ? 0 : 1) +
    (startDate === null ? 0 : 1) +
    (endDate === null ? 0 : 1);

  const resetFilterAction = event => {
    setIndustries([]);
    setEventTypes([]);
    setTitle('');
    setStartDate(null);
    setEndDate(null);
    if (typeof window !== 'undefined')
      window.history.replaceState(null, '', window.location.pathname);
  };

  const filteredByIndustry = conditionalFilter(
    children,
    industries.length > 0,
    child => industries.includes(child.props.industry)
  );

  const filteredByEventType = conditionalFilter(
    filteredByIndustry,
    eventTypes.length > 0,
    child => eventTypes.includes(child.props.eventType)
  );

  const filteredByDate = conditionalFilter(
    filteredByEventType,
    startDate !== null || endDate !== null,
    child => {
      const childDateOnly = child.props.date.substring(0, 10);
      return (
        (endDate !== null ? childDateOnly <= endDate : true) &&
        (startDate !== null ? childDateOnly >= startDate : true)
      );
    }
  );

  const filteredByTitle = conditionalFilter(
    filteredByDate,
    titleFilter !== '',
    child => child.props.title.toLowerCase().includes(titleFilter.toLowerCase())
  );

  const past = takeWhile(filteredByTitle, child => child.props.isPast);
  const upcoming = takeRightWhile(
    filteredByTitle,
    child => !child.props.isPast
  );

  const OnDemandSection = (
    <EventsSection events={past.reverse()} label={t('On-demand')} />
  );
  const UpcomingSection = (
    <EventsSection events={upcoming} label={t('Upcoming')} />
  );

  const EventsList = (
    <>
      {UpcomingSection}
      {OnDemandSection}
    </>
  );

  useEffect(() => forceCheck());

  return (
    <FilterSidebar
      content={EventsList}
      contentLength={filteredByTitle.length}
      activeFilterCount={activeFilterCount}
      resetFilterAction={resetFilterAction}
    >
      <InputFilter
        value={titleFilter}
        label={t('Event Name')}
        placeholder={t('Search by event')}
        updateFilter={updateTitleFilter}
      />

      <Filter
        activeOptions={industries}
        structure={createOptionsFilter(
          t('Industry'),
          industriesList,
          industriesKey
        )}
        updateFilter={updateIndustriesFilter}
        updateUrlParams={true}
      />

      <Filter
        activeOptions={eventTypes}
        structure={createOptionsFilter(
          t('Event Types'),
          eventTypesList,
          eventTypesKey
        )}
        updateFilter={updateEventTypesFilter}
        updateUrlParams={true}
      />

      <DateFilter
        start={startDate}
        end={endDate}
        label={t('Date')}
        updateFilter={updateDateFilter}
      />
    </FilterSidebar>
  );
};

export default clientOnly(EventCardsFilter);
